import { useState, useEffect, useRef } from "react";
import { mandateInvitationService, searchService, userService, clubService } from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";
import CustomDropdown from "../common/CustomDropdown";
import toast from "react-hot-toast";

const AddAgentModal = ({ onClose }) => {
  // Search states
  const [searchMode, setSearchMode] = useState("agent"); // 'agent' or 'agency'
  const [agentSearchQuery, setAgentSearchQuery] = useState("");
  const [agencySearchQuery, setAgencySearchQuery] = useState("");
  const [agentSearchResults, setAgentSearchResults] = useState([]);
  const [searchPagination, setSearchPagination] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Filter states
  const [selectedContinent, setSelectedContinent] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedNationality, setSelectedNationality] = useState("");

  // Agency states
  const [agencies, setAgencies] = useState([]);

  const [agencyAgents, setAgencyAgents] = useState({});

  // View states
  const [currentView, setCurrentView] = useState("search"); // 'search', 'agency-list', 'agency-detail'
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Form states (keeping setters for future API integration)
  // const [isSubmitting, setIsSubmitting] = useState(false);
  // const [errors, setErrors] = useState({});

  // Refs
  const agentSearchInputRef = useRef(null);
  const agencySearchInputRef = useRef(null);

  // Mock data for dropdowns
  const continents = [
    { value: "africa", label: "Africa" },
    { value: "asia", label: "Asia" },
    { value: "europe", label: "Europe" },
    { value: "north-america", label: "North America" },
    { value: "south-america", label: "South America" },
    { value: "oceania", label: "Oceania" },
  ];

  const countries = [
    { value: "usa", label: "United States" },
    { value: "canada", label: "Canada" },
    { value: "uk", label: "United Kingdom" },
    { value: "germany", label: "Germany" },
    { value: "france", label: "France" },
  ];

  const nationalities = [
    { value: "american", label: "American" },
    { value: "canadian", label: "Canadian" },
    { value: "british", label: "British" },
    { value: "german", label: "German" },
    { value: "french", label: "French" },
  ];

  // Debounced search
  const debouncedAgentSearchQuery = useDebounce(agentSearchQuery, 300);
  const debouncedAgencySearchQuery = useDebounce(agencySearchQuery, 300);

  // Search agents when query changes or page changes
  useEffect(() => {
    if (searchMode === "agent" && debouncedAgentSearchQuery.trim()) {
      searchAgents(debouncedAgentSearchQuery, 1);
      setCurrentPage(1);
    } else if (searchMode === "agent" && !debouncedAgentSearchQuery.trim()) {
      setAgentSearchResults([]);
      setSearchPagination(null);
      setIsSearching(false);
    }
  }, [debouncedAgentSearchQuery, searchMode]);

  // Handle agency search
  useEffect(() => {
    if (searchMode === "agency" && debouncedAgencySearchQuery.trim()) {
      searchAgencies(debouncedAgencySearchQuery);
      setCurrentView("agency-list");
    } else if (searchMode === "agency" && !debouncedAgencySearchQuery.trim()) {
      setCurrentView("search");
    }
  }, [debouncedAgencySearchQuery, searchMode]);

  // Focus search input when modal opens or search mode changes
  useEffect(() => {
    setTimeout(() => {
      if (searchMode === "agent" && agentSearchInputRef.current) {
        agentSearchInputRef.current.focus();
      } else if (searchMode === "agency" && agencySearchInputRef.current) {
        agencySearchInputRef.current.focus();
      }
    }, 100);
  }, [searchMode]);

  const searchAgents = async (query, page = 1) => {
    setIsSearching(true);
    try {
      const params = {
        type: "agents",
        query: query,
        per_page: 10,
        page: page,
      };

      const response = await searchService.search(params);

      if (response.success && response.data) {
        // Updated to handle new response structure
        const agents = response.data.results?.agents || [];
        setAgentSearchResults(agents);
        setSearchPagination(response.pagination || null);
      } else {
        setAgentSearchResults([]);
        setSearchPagination(null);
      }
    } catch (error) {
      console.error("Agent search error:", error);
      setAgentSearchResults([]);
      setSearchPagination(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePageChange = (page) => {
    if (agentSearchQuery.trim()) {
      setCurrentPage(page);
      searchAgents(agentSearchQuery, page);
    }
  };

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
  };

  const handleAgencySelect = async (agency) => {
    setSelectedAgency(agency);
    setCurrentView("agency-detail");
    await fetchAgencyProfile(agency.id);
  };

  const handleViewProfile = (agent) => {
    // Public agent profile lives at /search/agent/:agentId. Some search payloads
    // expose the FK as `account_id` (preferred) or fall back to `id`.
    const agentId = agent?.account_id || agent?.id;
    if (!agentId) return;
    window.open(`/search/agent/${agentId}`, "_blank");
  };

  const handleGoBack = () => {
    if (currentView === "agency-detail") {
      setCurrentView("agency-list");
      setSelectedAgency(null);
      setSelectedAgent(null);
    } else if (currentView === "agency-list") {
      setCurrentView("search");
    }
  };

  const handleSendRequest = async () => {
    setIsLoading(true);
    if (selectedAgent) {
      console.log("Sending request to agent:", selectedAgent);
      // Use account_id if available, otherwise use id
      const agentId = selectedAgent.account_id || selectedAgent.id;
      
      const response = await mandateInvitationService.sendRepresentationRequestToAgent(agentId);
      if (response.success) {
        toast.success(response.message || "Request sent successfully!");
      } else {
        toast.error(response.message || "Failed to send request. Please try again.");
      }
      onClose();
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedAgent(null);
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Search agencies function
  const searchAgencies = async (query) => {
    setIsSearching(true);
    try {
      const response = await searchService.searchAgencies(query);
      if (response.success) {
        // Updated to handle new response structure for searchAgencies
        const agenciesData = response.data.results?.agencies || response.data || [];
        setAgencies(agenciesData);
      } else {
        setAgencies([]);
      }
    } catch (error) {
      console.error("Agency search error:", error);
      setAgencies([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Fetch agency profile with agents
  const fetchAgencyProfile = async (agencyId) => {
    setIsSearching(true);
    try {
      // Use the agency public profile API
      const response = await clubService.getAgencyPublicProfile(agencyId);
      
      if (response.success && response.data) {
        // Extract agents from the agency profile response
        const agencyData = response.data.agency;
        if (agencyData && agencyData.agents) {
          // Combine licensed and unlicensed agents into a single array
          const allAgents = [
            ...agencyData.agents
          ];
          
          // Store agents for this agency
          setAgencyAgents(prevState => ({
            ...prevState,
            [agencyId]: allAgents || []
          }));
        }
      }
    } catch (error) {
      console.error("Fetch agency profile error:", error);
      // Set empty array for this agency if there's an error
      setAgencyAgents(prevState => ({
        ...prevState,
        [agencyId]: []
      }));
    } finally {
      setIsSearching(false);
    }
  };

  const getFilteredAgencies = () => {
    return agencies;
  };

  const getCurrentAgents = () => {
    if (currentView === "agency-detail" && selectedAgency) {
      return agencyAgents[selectedAgency.id] || [];
    }
    return agentSearchResults;
  };

  const handleClickOutside = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleClickOutside}
    >
      <div className="bg-white rounded-lg w-full max-w-5xl  max-h-[85vh] flex flex-col">
        {/* Modal Header - Always Visible */}
        <div className="flex items-center justify-between py-6 px-2 border-b border-[#D6DBEA] mx-6">
          <h2 className="text-[16px] font-bold text-[#3C3C3C]">FIND AGENT</h2>

          <div className="flex items-center gap-4">
            <CustomDropdown
              options={continents}
              value={selectedContinent}
              onChange={setSelectedContinent}
              placeholder="Continent"
              className="w-40"
            />
            <CustomDropdown
              options={countries}
              value={selectedCountry}
              onChange={setSelectedCountry}
              placeholder="Country"
              className="w-32"
            />
            <CustomDropdown
              options={nationalities}
              value={selectedNationality}
              onChange={setSelectedNationality}
              placeholder="Nationality"
              className="w-32"
            />
          </div>
        </div>

        {/* Search Inputs - Always Visible */}
        <div className="p-6">
          <div className="relative">
            <div className="grid grid-cols-2 gap-8">
              {/* Agent Search */}
              <div className="relative">
                <input
                  ref={agentSearchInputRef}
                  type="text"
                  placeholder="Search agent by name"
                  value={agentSearchQuery}
                  onChange={(e) => {
                    setAgentSearchQuery(e.target.value);
                    setSearchMode("agent");
                    setAgencySearchQuery("");
                    setCurrentView("search");
                    setAgentSearchResults([]);
                    setSearchPagination(null);
                  }}
                  className="w-full h-12 pl-12 pr-4 border border-[#D2D4D8] rounded-[5px] text-base placeholder-[#626262] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg
                  className="w-5 h-5 text-[#626262] absolute left-4 top-1/2 -translate-y-1/2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {isSearching && searchMode === "agent" && !currentView.includes("agency") && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                  </div>
                )}
              </div>

              {/* Agency Search */}
              <div className="relative">
                <input
                  ref={agencySearchInputRef}
                  type="text"
                  placeholder="Browse by Agency"
                  value={agencySearchQuery}
                  onChange={(e) => {
                    setAgencySearchQuery(e.target.value);
                    setSearchMode("agency");
                    setAgentSearchQuery("");
                    setCurrentView(e.target.value.trim() ? "agency-list" : "search");
                    setAgentSearchResults([]);
                    setSearchPagination(null);
                  }}
                  className="w-full h-12 pl-12 pr-4 border border-[#D2D4D8] rounded-[5px] text-base placeholder-[#626262] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg
                  className="w-5 h-5 text-[#626262] absolute left-4 top-1/2 -translate-y-1/2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <svg
                  className="w-5 h-5 text-[#626262] absolute right-4 top-1/2 -translate-y-1/2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            {/* OR Text in the middle */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3">
              <span className="text-[#1E1E1E] font-bold">or</span>
            </div>
          </div>
        </div>

        {/* Agency Header (when viewing agency detail) - Below inputs */}
        {currentView === "agency-detail" && selectedAgency && (
          <div className="mx-6 py-4 bg-white border-b border-[#D6DBEA]">
            <div className="flex items-center gap-4">
              <button
                onClick={handleGoBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.6673 6.00016H1.33398M1.33398 6.00016L6.00065 10.6668M1.33398 6.00016L6.00065 1.3335"
                    stroke="#1E1E1E"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>  
              </button>
              <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white font-bold text-sm">
                {selectedAgency.image_path ? (
                  <img
                    src={selectedAgency.image_path}
                    alt={selectedAgency.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  getInitials(selectedAgency.name)
                )}
              </div>
              <h3 className="text-[14px] font-medium text-[#0A0F2D]">
                {selectedAgency.name}
              </h3>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Agency List View */}
          {currentView === "agency-list" && (
            <div className="p-6">
              {isSearching && searchMode === "agency" ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                    <p className="text-gray-600">Searching agencies...</p>
                  </div>
                </div>
              ) : getFilteredAgencies().length > 0 ? (
                <div className="space-y-3">
                  {getFilteredAgencies().map((agency) => (
                    <div
                      key={agency.id}
                      className="grid grid-cols-3 items-center p-4 border-b border-[#D6DBEA] hover:bg-[#F8F9FB] transition-colors cursor-pointer"
                      onClick={() => handleAgencySelect(agency)}
                    >
                      {/* First Column: Image and Name */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white font-bold text-sm">
                          {agency.image_path ? (
                            <img
                              src={agency.image_path}
                              alt={agency.name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            getInitials(agency.name)
                          )}
                        </div>
                        <span className="text-[14px] text-[#0A0F2D] font-medium truncate">
                          {agency.name}
                        </span>
                      </div>

                      {/* Second Column: View Profile */}
                      <div className="text-center">
                        <span className="text-[14px] text-[#1A212F]">
                          View profile
                        </span>
                      </div>

                      {/* Third Column: Agent Count and Arrow */}
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-[14px] text-[#1A212F]">
                          {agency.agent_count} agents
                        </span>
                        <svg
                          className="w-4 h-4 text-[#1A212F]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    <p className="text-gray-600">No agencies found</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Try adjusting your search terms
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Agent Results (for both search and agency detail) */}
          {(currentView === "search" || currentView === "agency-detail") && (
            <div className="p-6">
              {isSearching &&
              getCurrentAgents().length === 0 &&
              searchMode === "agent" ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                    <p className="text-gray-600">Searching agents...</p>
                  </div>
                </div>
              ) : getCurrentAgents().length > 0 ? (
                <div className="space-y-4">
                  {/* Results List */}
                  <div className="space-y-0">
                    {getCurrentAgents().map((agent) => (
                      <div
                        key={agent.id}
                        className={`p-4 border-b border-[#D6DBEA] transition-colors ${
                          selectedAgent?.id === agent.id
                            ? "bg-blue-50"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className="w-12 h-12 rounded-full bg-gray-300 bg-cover bg-center flex-shrink-0 flex items-center justify-center cursor-pointer"
                            onClick={() => handleAgentSelect(agent)}
                          >
                            {agent.image_path ? (
                              <img
                                src={agent.image_path}
                                alt={agent.name}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-gray-600 font-medium">
                                {getInitials(agent.name)}
                              </span>
                            )}
                          </div>

                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => handleAgentSelect(agent)}
                          >
                            <h3 className="text-base font-medium text-[#1A212F] truncate">
                              {agent.name}
                            </h3>
                          </div>

                          {/* View Profile Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewProfile(agent);
                            }}
                            className="px-4 py-2 text-[14px] text-[#1A212F] bg-white hover:bg-[#F8F9FB] transition-colors"
                          >
                            View profile
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination (only for search results) */}
                  {currentView === "search" && searchPagination && (
                    <div className="pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          Showing {getCurrentAgents().length} of{" "}
                          {searchPagination.count || 0} results
                          {searchPagination.total_pages > 1 && (
                            <span className="ml-2">
                              (Page {currentPage} of{" "}
                              {searchPagination.total_pages})
                            </span>
                          )}
                        </div>

                        {(searchPagination.total_pages > 1 ||
                          searchPagination.has_more) && (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage <= 1}
                              className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              Previous
                            </button>

                            <span className="px-2 text-sm text-gray-600">
                              {currentPage}
                            </span>

                            <button
                              type="button"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={
                                !searchPagination.has_more &&
                                currentPage >= searchPagination.total_pages
                              }
                              className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              Next
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : searchMode === "agent" &&
                agentSearchQuery.trim() &&
                !isSearching ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    <p className="text-gray-600">
                      No agents found for "{agentSearchQuery}"
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Try adjusting your search terms
                    </p>
                  </div>
                </div>
              ) : currentView === "search" ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <p className="text-gray-600">
                      Start typing to search for agents or browse by agency
                    </p>
                  </div>
                </div>
              ) : currentView === "agency-detail" &&
                getCurrentAgents().length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    <p className="text-gray-600">
                      No agents found in this agency
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Action Buttons (when agent is selected) */}
        {selectedAgent && (
          <div className="p-6 border-t bg-gray-50">
            <div className="flex items-center justify-start gap-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-white border border-[#E6E6E6] rounded-lg text-[#1A212F] hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendRequest}
                className="px-4 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 transition-colors"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : `Send request to ${selectedAgent.name}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddAgentModal;
